// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * A first-party GGUF binary parser.
 *
 * It reads from a plain `Uint8Array` and throws `NeedMoreBytes` the moment it
 * runs off the end, which is what lets the same code drive a progressive Range
 * read of a remote file and a `Blob.slice` of a local one — see source.ts.
 *
 * Layout (ggml/docs/gguf.md):
 *   magic "GGUF" (4) | version u32 | tensor_count u64 | kv_count u64
 *   kv_count × ( key:string, value_type:u32, value )
 *   tensor_count × ( name:string, n_dims:u32, dims:u64[n_dims], type:u32, offset:u64 )
 *   padding to general.alignment
 *   tensor data
 *
 * where string = ( len:u64, utf8 bytes ).
 */

import {
  GgufValueType,
  NeedMoreBytes,
  NotGguf,
  type GgufTensorInfo,
  type GgufValue,
  type ParsedGguf,
} from './types';

const MAGIC_LE = 0x46554747; // "GGUF" read little-endian

/** Bytes per element for every fixed-width metadata scalar. */
const SCALAR_BYTES: Partial<Record<GgufValueType, number>> = {
  [GgufValueType.UINT8]: 1,
  [GgufValueType.INT8]: 1,
  [GgufValueType.UINT16]: 2,
  [GgufValueType.INT16]: 2,
  [GgufValueType.UINT32]: 4,
  [GgufValueType.INT32]: 4,
  [GgufValueType.FLOAT32]: 4,
  [GgufValueType.BOOL]: 1,
  [GgufValueType.UINT64]: 8,
  [GgufValueType.INT64]: 8,
  [GgufValueType.FLOAT64]: 8,
};

/**
 * Arrays longer than this are walked but not materialised. The tokenizer's
 * token list is routinely 150k strings; turning those into JS objects costs
 * far more than the parse itself and we never look at them.
 */
const MAX_MATERIALISED_ARRAY = 512;

const decoder = new TextDecoder('utf-8');

class Cursor {
  readonly dv: DataView;
  p = 0;

  constructor(readonly buf: Uint8Array, public le = true) {
    this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  private need(n: number): void {
    if (n < 0 || this.p + n > this.buf.byteLength) throw new NeedMoreBytes(this.p + Math.max(n, 0));
  }

  u32(): number {
    this.need(4);
    const v = this.dv.getUint32(this.p, this.le);
    this.p += 4;
    return v;
  }

  i32(): number {
    this.need(4);
    const v = this.dv.getInt32(this.p, this.le);
    this.p += 4;
    return v;
  }

  /**
   * u64 as a JS number. Lengths and offsets in a GGUF are file positions, which
   * stay far inside 2^53 (9 PB) — but a corrupt or hostile file could claim
   * otherwise, so anything past MAX_SAFE_INTEGER is rejected rather than
   * silently rounded.
   */
  u64(): number {
    this.need(8);
    const v = this.dv.getBigUint64(this.p, this.le);
    this.p += 8;
    if (v > BigInt(Number.MAX_SAFE_INTEGER)) throw new NotGguf('64-bit value too large to be real');
    return Number(v);
  }

  i64(): bigint {
    this.need(8);
    const v = this.dv.getBigInt64(this.p, this.le);
    this.p += 8;
    return v;
  }

  f32(): number {
    this.need(4);
    const v = this.dv.getFloat32(this.p, this.le);
    this.p += 4;
    return v;
  }

  f64(): number {
    this.need(8);
    const v = this.dv.getFloat64(this.p, this.le);
    this.p += 8;
    return v;
  }

  u8(): number {
    this.need(1);
    return this.dv.getUint8(this.p++);
  }

  i8(): number {
    this.need(1);
    return this.dv.getInt8(this.p++);
  }

  u16(): number {
    this.need(2);
    const v = this.dv.getUint16(this.p, this.le);
    this.p += 2;
    return v;
  }

  i16(): number {
    this.need(2);
    const v = this.dv.getInt16(this.p, this.le);
    this.p += 2;
    return v;
  }

  skip(n: number): void {
    this.need(n);
    this.p += n;
  }

  string(): string {
    const len = this.u64();
    this.need(len);
    const s = decoder.decode(this.buf.subarray(this.p, this.p + len));
    this.p += len;
    return s;
  }

  /** Walk past a string without decoding it. */
  skipString(): void {
    this.skip(this.u64());
  }
}

function readScalar(c: Cursor, type: GgufValueType): number | boolean | bigint {
  switch (type) {
    case GgufValueType.UINT8:
      return c.u8();
    case GgufValueType.INT8:
      return c.i8();
    case GgufValueType.UINT16:
      return c.u16();
    case GgufValueType.INT16:
      return c.i16();
    case GgufValueType.UINT32:
      return c.u32();
    case GgufValueType.INT32:
      return c.i32();
    case GgufValueType.FLOAT32:
      return c.f32();
    case GgufValueType.BOOL:
      return c.u8() !== 0;
    case GgufValueType.UINT64:
      return c.u64();
    case GgufValueType.INT64:
      return c.i64();
    case GgufValueType.FLOAT64:
      return c.f64();
    default:
      throw new NotGguf(`unknown metadata value type ${type}`);
  }
}

function readValue(c: Cursor, type: GgufValueType): GgufValue {
  if (type === GgufValueType.STRING) return { kind: 'string', value: c.string() };

  if (type === GgufValueType.ARRAY) {
    const elemType = c.u32() as GgufValueType;
    const count = c.u64();
    const elemStart = c.p;
    const materialise = count <= MAX_MATERIALISED_ARRAY;
    const values: unknown[] | null = materialise ? [] : null;

    if (elemType === GgufValueType.STRING) {
      // Strings are variable-length, so there is no shortcut: we must walk each
      // length prefix. We just avoid decoding the bytes when we don't need them.
      for (let i = 0; i < count; i++) {
        if (materialise) values!.push(c.string());
        else c.skipString();
      }
    } else if (elemType === GgufValueType.ARRAY) {
      for (let i = 0; i < count; i++) {
        const nested = readValue(c, GgufValueType.ARRAY);
        if (materialise) values!.push(nested);
      }
    } else {
      const width = SCALAR_BYTES[elemType];
      if (width === undefined) throw new NotGguf(`unknown array element type ${elemType}`);
      if (materialise) {
        for (let i = 0; i < count; i++) values!.push(readScalar(c, elemType));
      } else {
        c.skip(width * count);
      }
    }
    return { kind: 'array', elemType, count, values, elemStart };
  }

  return { kind: 'scalar', type, value: readScalar(c, type) };
}

export interface ParseProgress {
  /** 0..1 through the KV block, then 0..1 through the tensor block. */
  phase: 'kv' | 'tensors';
  done: number;
  total: number;
}

/**
 * Parse a GGUF header + tensor table out of `buf`.
 *
 * Throws `NeedMoreBytes` if `buf` is a truncated prefix of the file — the
 * caller is expected to fetch more and try again from the top. Re-parsing from
 * byte 0 is deliberate: it costs a few milliseconds and removes any chance of
 * resumption state disagreeing with a fresh parse.
 */
export function parseGguf(buf: Uint8Array, onProgress?: (p: ParseProgress) => void): ParsedGguf {
  if (buf.byteLength < 24) throw new NeedMoreBytes(24);

  const c = new Cursor(buf, true);
  const magic = c.u32();
  if (magic !== MAGIC_LE) {
    throw new NotGguf(
      'Not a GGUF file — the first four bytes are not "GGUF". If this is a safetensors or PyTorch checkpoint, it needs converting first.',
    );
  }

  // GGUF is little-endian by default. Big-endian files (built for s390x) carry
  // the same magic but a byte-swapped version, so a nonsense version number is
  // the detection signal rather than an error.
  let version = c.u32();
  let littleEndian = true;
  if (version > 0xffff) {
    littleEndian = false;
    c.le = false;
    c.p = 4;
    version = c.u32();
  }
  if (version < 2 || version > 3) {
    throw new NotGguf(`Unsupported GGUF version ${version}. This tool reads v2 and v3.`);
  }

  const tensorCount = c.u64();
  const kvCount = c.u64();
  if (tensorCount > 1_000_000 || kvCount > 100_000) {
    throw new NotGguf('Header claims an implausible number of tensors or metadata keys.');
  }

  const kv = new Map<string, GgufValue>();
  for (let i = 0; i < kvCount; i++) {
    const key = c.string();
    const type = c.u32() as GgufValueType;
    kv.set(key, readValue(c, type));
    if (onProgress && (i & 15) === 0) onProgress({ phase: 'kv', done: i, total: kvCount });
  }
  const kvBlockEnd = c.p;

  const tensors: GgufTensorInfo[] = new Array(tensorCount);
  for (let i = 0; i < tensorCount; i++) {
    const name = c.string();
    const nDims = c.u32();
    if (nDims > 4) throw new NotGguf(`Tensor "${name}" claims ${nDims} dimensions; ggml allows 4.`);
    const dims: number[] = new Array(nDims);
    let elements = 1;
    for (let d = 0; d < nDims; d++) {
      dims[d] = c.u64();
      elements *= dims[d];
    }
    const typeId = c.u32();
    const offset = c.u64();
    tensors[i] = { name, dims, typeId, offset, elements };
    if (onProgress && (i & 63) === 0) onProgress({ phase: 'tensors', done: i, total: tensorCount });
  }
  const tensorInfoEnd = c.p;

  const alignRaw = kv.get('general.alignment');
  const alignment =
    alignRaw?.kind === 'scalar' && typeof alignRaw.value === 'number' && alignRaw.value > 0
      ? alignRaw.value
      : 32;
  const dataStart = Math.ceil(tensorInfoEnd / alignment) * alignment;

  return {
    header: { version, littleEndian, tensorCount, kvCount },
    kv,
    tensors,
    kvBlockEnd,
    tensorInfoEnd,
    alignment,
    dataStart,
    bytesRead: buf.byteLength,
  };
}

// ── Metadata accessors ───────────────────────────────────────────────
// GGUF keys are namespaced by architecture (`qwen3.block_count`), so almost
// every lookup needs the arch prefix resolved first. These helpers keep that
// in one place and always return `undefined` rather than throwing, because a
// missing key is a normal condition we surface in the UI.

export function metaString(kv: Map<string, GgufValue>, key: string): string | undefined {
  const v = kv.get(key);
  return v?.kind === 'string' ? v.value : undefined;
}

export function metaNumber(kv: Map<string, GgufValue>, key: string): number | undefined {
  const v = kv.get(key);
  if (v?.kind !== 'scalar') return undefined;
  if (typeof v.value === 'number') return v.value;
  if (typeof v.value === 'bigint') return Number(v.value);
  return undefined;
}

export function metaBool(kv: Map<string, GgufValue>, key: string): boolean | undefined {
  const v = kv.get(key);
  return v?.kind === 'scalar' && typeof v.value === 'boolean' ? v.value : undefined;
}

/**
 * Pull specific entries out of a string array that was walked but not
 * materialised.
 *
 * The tokenizer's vocabulary is 128k–260k strings, and building all of them to
 * read two would cost more than the entire parse. A chat template needs the
 * BOS and EOS token strings and nothing else, so this walks the length
 * prefixes to the wanted indices and decodes only those.
 */
export function pickFromStringArray(
  buf: Uint8Array,
  value: GgufValue,
  indices: number[],
): Map<number, string> {
  const out = new Map<number, string>();
  if (value.kind !== 'array' || value.elemType !== GgufValueType.STRING) return out;

  const wanted = new Set(indices.filter((i) => i >= 0 && i < value.count));
  if (!wanted.size) return out;
  const last = Math.max(...wanted);

  const c = new Cursor(buf);
  c.p = value.elemStart;
  try {
    for (let i = 0; i <= last; i++) {
      if (wanted.has(i)) out.set(i, c.string());
      else c.skipString();
    }
  } catch {
    // A truncated buffer here costs us a nicety, not the result.
  }
  return out;
}

/**
 * Read a value that may be either a scalar or a per-layer array.
 *
 * `{arch}.attention.head_count_kv` is a plain integer on most models but an
 * array with one entry per layer on models with non-uniform attention (some
 * hybrid and MoE architectures). Returning the array lets the KV-cache maths
 * sum per layer instead of assuming uniformity.
 */
export function metaNumberOrArray(
  kv: Map<string, GgufValue>,
  key: string,
): { uniform: number } | { perLayer: number[] } | undefined {
  const v = kv.get(key);
  if (!v) return undefined;
  if (v.kind === 'scalar') {
    const n = typeof v.value === 'bigint' ? Number(v.value) : v.value;
    return typeof n === 'number' ? { uniform: n } : undefined;
  }
  if (v.kind === 'array' && v.values) {
    const nums = v.values.map((x) => (typeof x === 'bigint' ? Number(x) : x)).filter((x): x is number => typeof x === 'number');
    if (nums.length === v.count && nums.length > 0) return { perLayer: nums };
  }
  return undefined;
}
